"use client"

import { useState } from "react"
import { SiteHeader } from "@/components/site-header"
import { SiteFooter } from "@/components/site-footer"
import { SITE_STATS } from "@/lib/site-stats"
import Link from "next/link"
import {
  FileText,
  Mail,
  ArrowRight,
  Rss,
  Calculator,
  BookOpen,
} from "lucide-react"
import { ProductShareButtons } from "@/components/blocks/product-share-buttons"
import { CTABand } from "@/components/brand-kit/cta-band"
import { Breadcrumb } from "@/components/brand-kit/breadcrumb"
import { pressBreadcrumbs } from "@/lib/breadcrumbs"
import { PRESS_OUTLETS } from "@/lib/press-outlets"
import { FeaturedInBand } from "@/components/press/featured-in-band"
import { PressArticleCard } from '@/components/press/press-article-card'
import type { PressCardImageData } from '@/lib/press-card-image'

interface PressRelease {
  id: number
  slug: string
  title: string
  summary: string | null
  published_at: string
  ein_url: string | null
  image: string | null
  topics: string[] | null
  cardImage?: PressCardImageData | null
  /**
   * FRIDGE-603 — the per-release enrichment blob (keyFindings etc.). The card
   * renders keyFindings[0] as the one-line quotable stat; releases without
   * banked findings render no chip (Real-or-Hidden, never a filler line).
   */
  enrichment?: { keyFindings?: string[] } | null
}

/** The one-line quotable stat for a release card, or null. */
function quotableStat(pr: PressRelease): string | null {
  const f = pr.enrichment?.keyFindings?.[0]
  return typeof f === 'string' && f.trim().length >= 20 ? f.trim() : null
}

interface PressClientProps {
  pressReleases: PressRelease[]
}

// The verified "As Featured In" outlets now live in ONE shared module
// (lib/press-outlets.ts) so /press and the site footer's trust band can never
// drift. Evidence per outlet: docs/Operations.md.
const FEATURED_IN = [...PRESS_OUTLETS]

const TOPIC_MAP: Record<string, string> = {
  'research': 'Research',
  'energy': 'Energy',
  'energy costs': 'Energy',
  'energy efficiency': 'Energy',
  'energy burden': 'Energy',
  'energy star': 'Energy',
  'climate': 'Energy',
  'rankings': 'Rankings',
  'data': 'Data',
  'local data': 'Data',
  'rebates': 'Rebates',
  'utility programs': 'Rebates',
  'consumer savings': 'Rebates',
  'housing': 'Housing',
  'renters': 'Housing',
  'inequality': 'Housing',
  'consumer': 'Consumer',
  'rural': 'Rankings',
  'freezers': 'Consumer',
}

function getDisplayCategories(releases: PressRelease[]): string[] {
  const categorySet = new Set<string>()
  releases.forEach(pr => {
    pr.topics?.forEach(t => {
      const mapped = TOPIC_MAP[t]
      if (mapped) categorySet.add(mapped)
    })
  })
  return Array.from(categorySet).sort()
}

function prMatchesCategory(pr: PressRelease, category: string): boolean {
  return (pr.topics || []).some(t => TOPIC_MAP[t] === category)
}

export default function PressClient({ pressReleases }: PressClientProps) {
  const [activeFilter, setActiveFilter] = useState<string | null>(null)
  const releaseCount = pressReleases.length
  const displayCategories = getDisplayCategories(pressReleases)

  const filtered = activeFilter
    ? pressReleases.filter(pr => prMatchesCategory(pr, activeFilter))
    : pressReleases

  return (
    <div className="ed-root min-h-screen bg-white">
      <SiteHeader />

      <main id="main-content">
        {/* HERO */}
        <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 36, paddingBottom: 36 }}>
            {/* ONE trail (lib/breadcrumbs.ts) — page.tsx emits the same
                pressBreadcrumbs() as the BreadcrumbList (FRIDGE-269). */}
            <Breadcrumb variant="editorial" trail={pressBreadcrumbs()} style={{ marginBottom: 24 }} />

            <div className="ed-eyebrow" style={{ marginBottom: 18 }}>
              <span className="dot" />
              ORIGINAL RESEARCH · {releaseCount} REPORTS · PRESS DISTRIBUTED
            </div>

            <h1 className="ed-h1" style={{ fontSize: 'clamp(40px, 5vw, 72px)' }}>
              Fridge.com{' '}
              <em style={{ fontStyle: 'italic', color: 'var(--fridge-orange)' }}>Press</em>
            </h1>
            <p className="ed-subtitle" style={{ maxWidth: 720, fontSize: 17, marginTop: 12 }}>
              Original research and data-driven reports on refrigeration efficiency, energy costs, and
              consumer appliance intelligence across the United States.
            </p>

            <div className="flex flex-wrap gap-4 text-sm text-gray-700 mt-6">
              <span>&#10003; Data-driven</span>
              <span>&#10003; Press distributed</span>
              <span>&#10003; Citable for media</span>
              <span>&#10003; Free to use</span>
            </div>

            <div style={{ marginTop: 18 }}>
              <ProductShareButtons productTitle="Fridge.com Press" pageType="press" variant="top" />
            </div>
          </div>
        </section>

        {/* STATS ROW */}
        <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 28, paddingBottom: 28 }}>
            <div className="ed-local-stats">
              <div className="cell tint">
                <div className="k">REPORTS</div>
                <div className="v">{releaseCount}</div>
                <div className="l">Research reports distributed via EIN Presswire.</div>
              </div>
              <div className="cell">
                <div className="k">CITIES</div>
                <div className="v">{SITE_STATS.cities}</div>
                <div className="l">Local-level coverage informing the data.</div>
              </div>
              <div className="cell">
                <div className="k">STATES</div>
                <div className="v">{SITE_STATS.states}</div>
                <div className="l">Geographic scope of underlying datasets.</div>
              </div>
              <div className="cell">
                <div className="k">VERIFIED MEDIA CITATIONS</div>
                <div className="v">{FEATURED_IN.length}</div>
                <div className="l">U.S. and international outlets.</div>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED IN — uniform grid for clean alignment. Self-hides when no verified outlets. */}
        {FEATURED_IN.length > 0 && (
        <section style={{ borderBottom: '1px solid #f3f4f6', background: 'var(--gray-50)' }}>
          <div className="ed-container" style={{ paddingTop: 40, paddingBottom: 40 }}>
            <div className="ed-eyebrow" style={{ marginBottom: 18, display: 'flex', justifyContent: 'center' }}>
              <span className="dot" />
              AS FEATURED IN
            </div>
            <FeaturedInBand heading="" />
          </div>
        </section>
        )}

        {/* PRESS RELEASES */}
        <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Press releases</h2>
              <span className="meta">{releaseCount} reports · EIN Presswire</span>
            </div>

            {displayCategories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                <button
                  onClick={() => setActiveFilter(null)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    activeFilter === null
                      ? 'bg-[#0469f0] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                {displayCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveFilter(activeFilter === category ? null : category)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      activeFilter === category
                        ? 'bg-[#0469f0] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            )}

            {filtered.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
                <p className="text-gray-500">No press releases found for this filter.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {filtered.map((pr) => (
                  <PressArticleCard key={pr.id} release={pr} finding={quotableStat(pr)} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* PRESS RESOURCES */}
        <section style={{ borderBottom: '1px solid #f3f4f6', background: 'var(--gray-50)' }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Press resources</h2>
              <span className="meta">media kit · RSS · media inquiries · articles · tools</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                // FRIDGE-703 — the media kit: boilerplate, verified numbers, datasets, logos, how to cite.
                { icon: <FileText className="w-5 h-5" />, title: 'Media Kit', desc: 'Boilerplate, numbers, datasets, logos', href: '/press/media-kit', external: false },
                { icon: <Rss className="w-5 h-5" />, title: 'RSS Feed', desc: 'Subscribe for new releases', href: '/press/feed.xml', external: true },
                { icon: <Mail className="w-5 h-5" />, title: 'Media Inquiries', desc: 'Contact our press team', href: '/contact', external: false },
                { icon: <BookOpen className="w-5 h-5" />, title: 'Expert Articles', desc: `${SITE_STATS.articles} buying guides`, href: '/blogs/news', external: false },
                { icon: <Calculator className="w-5 h-5" />, title: 'Free Tools', desc: `${SITE_STATS.tools} calculators`, href: '/tools', external: false },
              ].map((item, i) => {
                const cardClass =
                  "bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md hover:border-[#ff9951] transition-all group h-full flex flex-col"
                const inner = (
                  <>
                    <div className="w-10 h-10 rounded-lg bg-[#ff9951]/10 flex items-center justify-center text-[#ff9951] mb-3">
                      {item.icon}
                    </div>
                    <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-1">
                      RESOURCE · {String(i + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-semibold text-base text-gray-900 group-hover:text-[#ff9951] transition-colors mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </>
                )
                return item.external ? (
                  <a key={i} href={item.href} className={cardClass}>
                    {inner}
                  </a>
                ) : (
                  <Link key={i} href={item.href} className={cardClass}>
                    {inner}
                  </Link>
                )
              })}
            </div>

            {/* FRIDGE-603 — using our data: licensing, citation guidance,
                methodology. The three answers a journalist needs before
                lifting a number, on the page they land on. */}
            <div className="mt-8 bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-5 sm:px-6 py-3.5 border-b border-gray-200">
                <h3 className="text-sm sm:text-[15px] font-bold tracking-tight text-gray-900 m-0">
                  Using Fridge.com data
                </h3>
              </div>
              <div className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
                <div className="p-5">
                  <p className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] mb-1.5">LICENSE</p>
                  <p className="text-sm text-gray-600 leading-relaxed m-0">
                    Every published dataset and finding is free to reuse under{' '}
                    <a href="https://creativecommons.org/licenses/by/4.0/" rel="noopener noreferrer" target="_blank" className="text-[#0469f0] font-medium hover:underline">CC BY 4.0</a>
                    {' '}— use it in articles, broadcasts, and research with attribution.
                  </p>
                </div>
                <div className="p-5">
                  <p className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] mb-1.5">HOW TO CITE</p>
                  <p className="text-sm text-gray-600 leading-relaxed m-0">
                    Credit &ldquo;Fridge.com&rdquo; and link the report page. Each release carries a
                    copy-ready citation kit — APA, MLA, Chicago, AP, and social formats — on its own page.
                  </p>
                </div>
                <div className="p-5">
                  <p className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] mb-1.5">METHODOLOGY</p>
                  <p className="text-sm text-gray-600 leading-relaxed m-0">
                    How the numbers are built — data universe, matched-model method, and limitations — is
                    documented at{' '}
                    <Link href="/data/methodology" className="text-[#0469f0] font-medium hover:underline">fridge.com/data/methodology</Link>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA BAND */}
        <CTABand
          eyebrow="Media inquiries"
          title="Fridge.com research is available for citation"
          body="For press inquiries, data requests, or partnership opportunities, contact our team or subscribe to the RSS feed."
          buttonLabel="Contact Fridge.com"
          buttonHref="/contact"
        />

        {/* Bottom share */}
        <div className="ed-container" style={{ paddingTop: 32, paddingBottom: 40 }}>
          <ProductShareButtons productTitle="Fridge.com Press" pageType="press" variant="bottom" />
        </div>
      </main>

      <SiteFooter />
    </div>
  )
}
